import React, { useEffect, useState, useMemo, useRef } from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import WarningIcon from '@material-ui/icons/Warning';
import { Tooltip } from '@material-ui/core';
import styled from 'styled-components';
import { probeVideo, ProbedVideoInfo } from '../../../services/sessionsUpload';
import { timeSpanToSeconds, Video } from '../../../services/sessionModels';

export type TVideoInfo = ProbedVideoInfo & { url: string };

interface Props {
  url?: string | null;
  baseFps?: number;
  baseWidth?: number;
  baseHeight?: number;
  ok: (videoInfo: TVideoInfo) => void;
}

const supportedCodecs = ['hevc', 'h264', 'h265'];

const checkFps = (ret: TTotalStatus, fps: number | undefined | null, baseFps?: number) => {
  if (!fps) {
    ret.fps.error.push('Could not determine framerate');
  }
  if (baseFps && fps !== baseFps) {
    ret.fps.warning.push('Invalid framerate (' + fps + '), all videos must be of the same framerate (' + baseFps + ')');
  }
  if (fps && fps < 24) {
    ret.fps.error.push('Framerate (' + fps + ') too low, minimum is 24');
  }
  if (fps && fps > 60) {
    ret.fps.error.push('Framerate (' + fps + ') too high, maximum is 60');
  }
  if (fps && fps > 30) {
    ret.fps.warning.push('Framerate (' + fps + ') is high, this requires more computational power');
  }
};

const checkResolution = (
  ret: TTotalStatus,
  width: number | undefined | null,
  height: number | undefined | null,
  baseWidth?: number,
  baseHeight?: number
) => {
  if (!width || !height) {
    ret.resolution.error.push('Could not determine video resolution');
    return;
  }
  if ((baseHeight && height !== baseHeight) || (baseWidth && width !== baseWidth)) {
    ret.resolution.error.push(
      'Invalid resolution (' +
        width +
        'x' +
        height +
        '), all videos must be of the same resolution (' +
        baseWidth +
        'x' +
        baseHeight +
        ')'
    );
    return;
  }
  if (width < 1080) {
    ret.resolution.error.push('Video is not wide enough (' + width + '), it must be at least 1080');
  }
  if (width > 4096) {
    ret.resolution.error.push('Video is too wide  (' + width + '), it must be max 4096');
  }
  if (height < 720) {
    ret.resolution.error.push('Video is not high enough (' + height + '), it must be at least 720');
  }
  if (height > 4096) {
    ret.resolution.error.push('Video is too high (' + height + '), it must be max 4096');
  }
  if (height < 1080) {
    ret.resolution.warning.push('Video is not very high (' + height + '), quality can be impacted');
  }
  return ret;
};

const checkCodec = (ret: TTotalStatus, codec: string | undefined | null) => {
  if (!codec) {
    ret.codec.error.push('Could not determine video codec');
    return;
  }
  if (!supportedCodecs.includes(codec.toLocaleLowerCase())) {
    ret.codec.error.push('Unsupported video codec: ' + codec);
  }
};

const checkBitrate = (ret: TTotalStatus, bitrate: number | undefined | null) => {
  if (bitrate && bitrate > 0 && bitrate < 2000000) {
    ret.bitrate.warning.push('Video has a low bitrate (' + bitrate + '), output quality may be affected');
  }
};

const checkDuration = (ret: TTotalStatus, duration: number | undefined | null) => {
  if (duration && duration < 10 * 60) {
    ret.duration.warning.push('Video duration is very short (' + hms(duration) + ')');
  }
  if (duration && duration > 3 * 60 * 60) {
    ret.duration.warning.push('Video duration is very long (' + hms(duration) + ')');
  }
};

export const hms = (totalSeconds: number | undefined | null) => {
  if (totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return ('0' + hours).slice(-2) + ':' + ('0' + minutes).slice(-2) + ':' + ('0' + seconds).slice(-2);
  }
  return '-';
};

type TStatus = {
  error: Array<string>;
  warning: Array<string>;
};

type TTotalStatus = {
  fps: TStatus;
  resolution: TStatus;
  codec: TStatus;
  bitrate: TStatus;
  duration: TStatus;
};

const emptyStatus = (): TTotalStatus => ({
  fps: { error: [], warning: [] },
  resolution: { error: [], warning: [] },
  codec: { error: [], warning: [] },
  duration: { error: [], warning: [] },
  bitrate: { error: [], warning: [] },
});

export type TVideoCheckInfo = {
  fps?: number | null;
  width?: number | null;
  height?: number | null;
  codec?: string | null;
  bitrate?: number | null;
  duration?: number | null;
};

export const videoCheckInfo = (video: Pick<Video, 'fps' | 'width' | 'height' | 'encoding' | 'bitrate' | 'length'>): TVideoCheckInfo => ({
  fps: video.fps,
  width: video.width,
  height: video.height,
  codec: video.encoding,
  bitrate: video.bitrate,
  duration: timeSpanToSeconds(video.length),
});

export const checkVideo = (
  info: TVideoCheckInfo,
  baseFps?: number,
  baseWidth?: number,
  baseHeight?: number
): TTotalStatus => {
  const ret = emptyStatus();
  checkFps(ret, info.fps, baseFps);
  checkResolution(ret, info.width, info.height, baseWidth, baseHeight);
  checkCodec(ret, info.codec);
  checkBitrate(ret, info.bitrate);
  checkDuration(ret, info.duration);
  return ret;
};

export const videoProblems = (status: TTotalStatus) => {
  const all = [status.resolution, status.fps, status.codec, status.bitrate, status.duration];
  return {
    errors: all.flatMap((x) => x.error),
    warnings: all.flatMap((x) => x.warning),
  };
};

const hasError = (status?: TTotalStatus) => {
  if (!status) return false;
  return (
    status.fps.error.length > 0 ||
    status.bitrate.error.length > 0 ||
    status.resolution.error.length > 0 ||
    status.codec.error.length > 0 ||
    status.duration.error.length > 0
  );
};

const hasWarning = (status?: TTotalStatus) => {
  if (!status) return false;
  return (
    status.fps.warning.length > 0 ||
    status.bitrate.warning.length > 0 ||
    status.resolution.warning.length > 0 ||
    status.codec.warning.length > 0 ||
    status.duration.warning.length > 0
  );
};

const createTitleLine = (status: TStatus, prefix: string, value: string | number | undefined | null) => {
  if (status.error.length > 0) {
    return (
      <span>
        <ErrorIcon style={{ fontSize: 12, marginRight: 6 }} color="error" />
        {prefix}: {value} ({status.error.join(', ')})
      </span>
    );
  } else if (status.warning.length > 0) {
    return (
      <span>
        <WarningIcon style={{ fontSize: 12, marginRight: 6 }} color="primary" />
        {prefix}: {value} ({status.warning.join(', ')})
      </span>
    );
  } else {
    return (
      <span>
        <CheckCircleIcon style={{ fontSize: 12, marginRight: 6 }} color="secondary" />
        {prefix}: {value}
      </span>
    );
  }
};

const createTitle = (status: TTotalStatus | undefined, info: TVideoInfo | undefined) => {
  if (!status || !info) return;
  const fps = createTitleLine(status.fps, 'Framerate', info.fps);
  const resolution = createTitleLine(status.resolution, 'Resolution', info.width + 'x' + info.height);
  const codec = createTitleLine(status.codec, 'Codec', info.encoding);
  const duration = createTitleLine(status.duration, 'Duration', hms(timeSpanToSeconds(info.length)));
  const bitrate = createTitleLine(status.bitrate, 'Bitrate', info.bitrate);
  return (
    <div>
      {resolution}
      <br />
      {fps}
      <br />
      {codec}
      <br />
      {bitrate}
      <br />
      {duration}
      <br />
    </div>
  );
};

const VideoStatus: React.FunctionComponent<Props> = ({ url, ok, baseFps, baseWidth, baseHeight }) => {
  const [info, setInfo] = useState<TVideoInfo>();
  const [status, setStatus] = useState<TTotalStatus>();
  const [probeError, setProbeError] = useState<string>();
  const okRef = useRef(ok);
  okRef.current = ok;

  useEffect(() => {
    if (!url) return;
    setProbeError(undefined);
    let cancelled = false;
    probeVideo(url, baseFps)
      .then((probed) => {
        if (cancelled) return;
        const videoInfo = { ...probed, url };
        const ret = checkVideo(
          videoCheckInfo(probed),
          baseFps,
          baseWidth,
          baseHeight
        );
        setInfo(videoInfo);
        setStatus(ret);
        if (!hasError(ret)) {
          okRef.current(videoInfo);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        const message = String(error?.response?.data?.message ?? error?.response?.data ?? error?.message ?? 'Could not read video');
        setInfo(undefined);
        setStatus(undefined);
        setProbeError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [url, baseFps, baseWidth, baseHeight]);

  let title = useMemo(() => {
    return createTitle(status, info);
  }, [status, info]);

  let ret = <HelpIcon />;
  if (probeError) {
    ret = (
      <Tooltip title={probeError}>
        <ErrorIcon color="error" />
      </Tooltip>
    );
  } else if (hasError(status) && title) {
    ret = (
      <Tooltip title={title}>
        <ErrorIcon color="error" />
      </Tooltip>
    );
  } else if (hasWarning(status) && title) {
    ret = (
      <Tooltip title={title}>
        <WarningIcon color="primary" />
      </Tooltip>
    );
  } else if (title) {
    ret = (
      <Tooltip title={title}>
        <CheckCircleIcon color="secondary" />
      </Tooltip>
    );
  }

  return <Container>{ret}</Container>;
};

const Container = styled.div`
  margin: 16px;
`;

export default VideoStatus;
